import {
  Divider,
  Grid, GridColumn,
  Header,
  Icon,
  Placeholder,
  PlaceholderLine,
  Ref,
  Segment,
  Table,
} from '@mairistem/composants';
import { previewPj, type LigneMouvement, type PieceJointe } from '@mairistem/comptabilite';
import {
  formatMontant,
  getErrorMessage, IDENTIFIANT_NATURE_DOCUMENT, NATURES_DOCUMENTS,
  type TemplateRequestGet,
} from '@mairistem/finances-utils';
import { FileViewer } from '@mairistem/pdf';
import { find, isEmpty, isNil } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import './Mouvement.less';
import ZoomButton from './ZoomButton';
import type IMouvementProps from '../../../../interfaces/pieces/mouvements/liquidation/MouvementProps';
import getMouvementIcon from '../../../../utils/getMouvementIcon';
import renderMontant from '../../../../utils/mouvements/getMontantComptabilise';
import MouvementPJCount from './MouvementPJCount';
import getTotalPj from '../../../../utils/mouvements/getTotalPj';
import getPiecesJointes from '../../../../services/piecesJointes/piecesJointes';
import getLignesMouvements from '../../../../services/piecesComptables/mouvements/lignes/lignesMouvement';

const MouvementExpanded = ({
  rowData,
  onClick,
  onDoubleClick,
  exercice,
}: IMouvementProps) => {
  const [containerRef, setContainerRef] = React.useState<HTMLElement | null>(null);

  const [ligneMouvement, setLigneMouvement] = React.useState<LigneMouvement[]>([]);
  const [ligneMouvementMeta, setLigneMouvementMeta] = React.useState<TemplateRequestGet<LigneMouvement>['meta']>();
  const [ligneMouvementLoading, setLigneMouvementLoading] = React.useState(true);

  const [fileUrl, setFileUrl] = React.useState('');
  const [fileExtension, setFileExtension] = React.useState('');

  const [loading, setLoading] = React.useState(true);

  const { mouvement } = rowData;
  const { mtHt, mtTtc } = renderMontant({ mouvement, exercice });
  const handleGetLastFacture = (list: PieceJointe[]): PieceJointe | null => {
    let lastFacture: PieceJointe | null = null;
    const sortedList = list.sort((a, b) => a?.libelle?.localeCompare(b?.libelle));

    lastFacture = find(
      sortedList,
      (piece: PieceJointe) => piece.natureDocumind === find(
        NATURES_DOCUMENTS,
        { identifiant: IDENTIFIANT_NATURE_DOCUMENT.FACTURE },
      ).identifiant,
    );

    if (!lastFacture) {
      lastFacture = find(
        sortedList,
        (piece: PieceJointe) => piece.extension.replace('.', '') === 'pdf',
      );
    }

    return lastFacture;
  };

  const handleOnPreview = async (piece: PieceJointe): Promise<void> => {
    if (!piece) return;

    try {
      const file: Blob = await previewPj(piece.identifiant);
      const reader: FileReader = new FileReader();
      reader.onloadend = () => {
        setFileUrl(reader.result.toString());
        setFileExtension(piece.extension);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation du fichier.');
    }
  };

  const handleOpenNewTab = (link: string): void => {
    window.open(link, '_blank');
  };

  const handleRef = React.useCallback((node: HTMLElement | null) => {
    if (!isNil(node)) {
      setContainerRef(node);
    }
  }, []);

  const fetchLigneMouvement = React.useCallback(
    async () => {
      try {
        setLigneMouvementLoading(true);
        const { data, meta } = await getLignesMouvements(new URLSearchParams({
          identifiantMouvement: mouvement.identifiant.toString(),
          withLotMarche: 'true',
          withMarche: 'true',
          withImputation: 'true',
          perPage: '4',
        }));
        setLigneMouvement(data);
        setLigneMouvementMeta(meta);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Erreur lors du chargement des lignes de mouvement'));
      } finally {
        setLigneMouvementLoading(false);
      }
    },
    [mouvement.identifiant],
  );

  const fetchPiecesList = React.useCallback(
    async () => {
      try {
        const { data } = await getPiecesJointes(new URLSearchParams({
          nomFichierLie: 'cm_mouv',
          identifiantFichierLie: mouvement.identifiant.toString(),
          all: 'true',
        }));
        if (data.length > 0) {
          await handleOnPreview(handleGetLastFacture(data));
        }
      } catch (error) {
        toast.error(getErrorMessage(error, 'Erreur lors du chargement des pièces jointes'));
      }
    },
    [mouvement.identifiant],
  );

  const fetchData = React.useCallback(
    async () => {
      setLoading(true);
      await Promise.all([fetchLigneMouvement(), fetchPiecesList()]);
      setLoading(false);
    },
    [fetchLigneMouvement, fetchPiecesList],
  );

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPjCount = getTotalPj(mouvement);

  return (
    <Segment
      className="mouvement-expanded mouvement-container"
      onDoubleClick={onDoubleClick}
    >
      <ZoomButton onClick={onClick} alwaysVisible />
      <Grid columns={2}>
        <Grid.Column width={7}>
          <Grid columns={3}>
            <Grid.Row>
              <Grid.Column width={2} className="mouvement-row-icon" verticalAlign="middle">
                {getMouvementIcon(mouvement)}
                {' '}
                {mouvement.numero}
              </Grid.Column>
              <Grid.Column width={10} title={mouvement.objetPiece} className="mouvement-info-bold mouvement-objet-piece" verticalAlign="middle">
                {mouvement.objetPiece}
              </Grid.Column>
              <Grid.Column width={4} verticalAlign="middle" className="mouvement-info-bold mouvement-date">
                {new Date(mouvement.date).toLocaleDateString(
                  'fr-FR',
                  {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  },
                )}
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={2} />
              <Grid.Column width={10} title={mouvement.informationLien} className="mouvement-info-grey" verticalAlign="middle">
                {mouvement.informationLien}
              </Grid.Column>
              <Grid.Column width={4} />
            </Grid.Row>
          </Grid>
          {ligneMouvementLoading ? (
            <Placeholder>
              <Placeholder.Header>
                <PlaceholderLine />
                <PlaceholderLine />
              </Placeholder.Header>
              <Placeholder.Paragraph>
                <PlaceholderLine />
                <PlaceholderLine />
                <PlaceholderLine />
                <PlaceholderLine />
              </Placeholder.Paragraph>
            </Placeholder>
          ) : (
            <Grid as={Segment} basic fluid>
              <Grid.Row>
                <Grid.Column>
                  <Table basic="very" striped className="mouvement-expanded-table">
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell width={10}>
                          {ligneMouvement[0]?.identifiantTiers ? (
                            <>
                              <button
                                className="mouvement-tier-row active"
                                type="button"
                                onClick={() => handleOpenNewTab(`/tiers/${ligneMouvement[0].identifiantTiers}`)}
                              >
                                <Icon iconSet="Lucide" name="PersonStanding" size={20} />
                                <p>{mouvement.informationTiers}</p>
                              </button>
                              <p className="mouvement-info-grey">
                                {isEmpty(ligneMouvement[0].iban) ? null : `Viré sur ${ligneMouvement[0].iban}`}
                              </p>
                            </>
                          ) : (
                            <div className="mouvement-tier-row">
                              <Icon iconSet="Lucide" name="PersonStanding" size={20} />
                              <p>Aucun tiers renseigné</p>
                            </div>
                          )}
                        </Table.Cell>
                        <Table.Cell
                          width={3}
                          className={`mouvement-info-bold ${mtHt.comptabilise ? '' : 'mouvement-info-grey'}`}
                          textAlign="right"
                        >
                          {mtHt.value && (
                          <p>{`${formatMontant(mtHt.value, 2)} HT`}</p>
                          )}
                          {mtHt.comptabilise && <Icon name="Banknote" iconSet="Lucide" />}
                        </Table.Cell>

                        <Table.Cell
                          width={3}
                          textAlign="right"
                          className={`mouvement-info-bold ${mtTtc.comptabilise ? '' : 'mouvement-info-grey'}`}
                        >
                          <p>{`${formatMontant(mtTtc.value ?? 0, 2)} TTC`}</p>
                          {mtTtc.comptabilise && <Icon name="Banknote" iconSet="Lucide" />}
                        </Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table>
                </Grid.Column>
              </Grid.Row>
              <Divider fitted className="mouvement-expand-divider" />
              <Grid.Row>
                <Grid.Column>
                  <Table basic="very" striped compact className="mouvement-expanded-table">
                    <Table.Body>
                      {ligneMouvement.length === 0 && (
                        <Table.Row>
                          <Table.Cell colSpan={3} textAlign="center">
                            <p>Aucune ligne de mouvement à afficher.</p>
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {ligneMouvement.map((ligneMouvementItem) => {
                        let marcheDisplay = null;

                        if (ligneMouvementItem.lotMarche) {
                          if (ligneMouvementItem.lotMarche.marche.idContrat) {
                            marcheDisplay = ligneMouvementItem.lotMarche.marche.idContrat;
                          } else if (ligneMouvementItem.lotMarche.marche.code
                            && (ligneMouvementItem.lotMarche.marche.code.toLocaleLowerCase() !== 'simple')) {
                            marcheDisplay = ligneMouvementItem.lotMarche.marche.code;
                          } else if (ligneMouvementItem.lotMarche.code) {
                            marcheDisplay = ligneMouvementItem.lotMarche.code;
                          }
                        }

                        return (
                          <Table.Row key={ligneMouvementItem.identifiant}>
                            <Table.Cell width={10}>
                              {isEmpty(ligneMouvementItem?.imputation?.libelle) ? '-' : ligneMouvementItem?.imputation?.libelle}
                              <div className="mouvement-expanded-row-infos">
                                {marcheDisplay && (
                                  <div className="mouvement-expanded-infos-complementaires">
                                    <Icon iconSet="Lucide" name="Car" size={20} strokeWidth={1} color="grey" />
                                    <p title={marcheDisplay}>
                                      {marcheDisplay}
                                    </p>
                                  </div>
                                )}
                                {ligneMouvementItem.actif && (
                                  <div className="mouvement-expanded-infos-complementaires">
                                    <Icon iconSet="Lucide" name="Megaphone" size={20} strokeWidth={1} color="grey" />
                                    <p title={ligneMouvementItem.actif}>
                                      {ligneMouvementItem.actif}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </Table.Cell>
                            <Table.Cell
                              width={3}
                              className={mtHt.comptabilise ? 'mouvement-info-bold' : 'mouvement-info-grey'}
                              textAlign="right"
                            >
                              {ligneMouvementItem.mtht !== ligneMouvementItem.mttc && (
                                <>
                                  {formatMontant(ligneMouvementItem.mtht, 2)}
                                  {' '}
                                  HT
                                </>
                              )}
                            </Table.Cell>
                            <Table.Cell
                              width={3}
                              textAlign="right"
                              className={mtTtc.comptabilise ? 'mouvement-info-bold' : 'mouvement-info-grey'}
                            >
                              {formatMontant(ligneMouvementItem.mttc, 2)}
                              {' '}
                              TTC
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                    {ligneMouvementMeta.total > 4 && (
                      <Table.Footer fullWidth>
                        <Table.Row>
                          <Table.HeaderCell>
                            <Link to={`liquidations/${mouvement.identifiant}`}>
                              {ligneMouvementMeta.total - 4 > 1
                                ? `Voir les ${ligneMouvementMeta.total - 4} autres lignes en ouvrant le mandat`
                                : "Voir l'autre ligne en ouvrant le mandat"}
                            </Link>
                          </Table.HeaderCell>
                          <Table.HeaderCell />
                          <Table.HeaderCell />
                        </Table.Row>
                      </Table.Footer>
                    )}

                  </Table>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          )}
        </Grid.Column>
        <GridColumn width={9} floated="right">
          <Grid columns={2}>
            <Grid.Row>
              <Grid.Column width={14} verticalAlign="middle">
                <Ref innerRef={handleRef}>
                  <Segment
                    loading={loading}
                    className="viewer-document-pdf"
                    placeholder
                    textAlign="center"
                  >
                    {(!loading && fileUrl) && (
                      <FileViewer
                        extension={fileExtension}
                        containerRef={containerRef}
                        filePath={fileUrl}
                      />
                    )}
                    {(!loading && !fileUrl) && <Header as="h3">Aucun document PDF à afficher.</Header>}
                  </Segment>
                </Ref>
              </Grid.Column>
              {totalPjCount >= 0 && (
                <Grid.Column width={2} textAlign="right">
                  <MouvementPJCount totalPjCount={totalPjCount} />
                </Grid.Column>
              )}
            </Grid.Row>
          </Grid>

        </GridColumn>
      </Grid>
    </Segment>
  );
};

export default MouvementExpanded;
